import {
  brandOptions,
  organizations,
  suppliers,
  contactPersons,
  adminUnits,
} from './itemsFixtureData';

/**
 * Deterministic probes for the bulk-edit ATOMICITY epic (dl-58423 / DL-65589): rollback,
 * failure surfacing and role coverage of the "either all selected items update or none" guarantee.
 *
 * Isolable via `?search=AtomicityProbe` (a unique substring, absent from every generated/other
 * fixture name), so the atomicity specs address exactly these four rows and nothing else. The set
 * is deliberately four: three selectable rows (A/B/C) plus a fourth CONTROL row that the specs
 * leave unselected — without it, a write that touched every row in the table would pass unnoticed.
 *
 * Baselines are chosen to make "unchanged" meaningful, not vacuous (the epic's rule: "Unchanged
 * asserted against empty fields proves nothing"):
 * - `isActive: true` (Status = Active) so a failed attempt to set Inactive is DISTINGUISHABLE from
 *   the original — the row must still read Active, not the target Inactive.
 * - distinct, non-empty `defaultQty` per row (11 / 22 / 33 / 44) so a failed attempt to set a
 *   single shared Quantity (5) is likewise distinguishable, per row, from the original.
 *
 * Base shape mirrors `bulkUpdateProbeBase` (`bulkUpdateProbeFixtureData.ts`); every field a spec
 * asserts on is set explicitly here rather than inherited, so a later shared-base edit cannot
 * silently change what a test proves.
 */
const bulkAtomicityProbeBase = {
  organization: organizations[0],
  supplier: suppliers[0],
  companyContactPerson: contactPersons[0],
  supplierContactPerson: null,
  logo: null,
  suppliedBy: null,
  readyForExport: false,
  updatedAfterLastExport: false,
  lastUpdated: '2026-01-15T09:00:00',
  deadline: null,
  etd: null,
  erpItemId: null,
  nameGeneratorExtraText: null,
  physicalReferenceNumber: null,
  categories: [],
  customFields: [],
  brand: brandOptions[0],
  adminUnit: adminUnits[0],
  isActive: true,
};

export const bulkAtomicityProbeA = {
  ...bulkAtomicityProbeBase,
  id: 91001,
  name: 'AtomicityProbe A',
  userDefinedId: 'IT-91001',
  description: 'AtomicityProbe A original description',
  defaultQty: 11,
};
export const bulkAtomicityProbeB = {
  ...bulkAtomicityProbeBase,
  id: 91002,
  name: 'AtomicityProbe B',
  userDefinedId: 'IT-91002',
  description: 'AtomicityProbe B original description',
  defaultQty: 22,
};
export const bulkAtomicityProbeC = {
  ...bulkAtomicityProbeBase,
  id: 91003,
  name: 'AtomicityProbe C',
  userDefinedId: 'IT-91003',
  description: 'AtomicityProbe C original description',
  defaultQty: 33,
};
/** Never selected by the atomicity specs — the control that proves a write did not touch the
 *  whole table. */
export const bulkAtomicityProbeControl = {
  ...bulkAtomicityProbeBase,
  id: 91004,
  name: 'AtomicityProbe Control',
  userDefinedId: 'IT-91004',
  description: 'AtomicityProbe Control original description',
  defaultQty: 44,
};

export const bulkAtomicityItems = [
  bulkAtomicityProbeA,
  bulkAtomicityProbeB,
  bulkAtomicityProbeC,
  bulkAtomicityProbeControl,
];
